'use client';

import { Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { handleChange } from '../handleOnChange';
import Recaptcha from '../Recaptcha';
import { useStyles } from '../style';
import { validateFields } from '../validate';
import LoginDetails from './LoginDetails';
import { ACTIONS, SWEEPR_WIDGET_ERROR } from '../../../util/constants';
import HandleError from '../../shared/widget-error-handling/HandleError';

const { ERROR_TRY_AGAIN, ERROR_CLOSE } = ACTIONS;
const { TITLE, DESCRIPTION, TRY_AGAIN, CLOSE, TYPE } = SWEEPR_WIDGET_ERROR;

/**
 * @name LoginVerificationContainer
 * @description Login verification fields container
 * @param {*} props - React props
 * @returns {JSX.Element} Login Verification Container
 */
const LoginVerificationContainer = props => {
  const theme = useTheme();
  const style = useStyles(theme);
  const fieldsRef = useRef({});
  const {
    setValues,
    setDisableCTA,
    values,
    setCaptchaV2,
    apiError,
    enableCaptchaV2,
    captchaV2,
    errorCount,
    maxErrorCount,
  } = props;
  const recaptchaRef = useRef(null);
  const [errors, setErrors] = useState({
    email: null,
    password: null,
  });

  /**
   * @name onChange - Function to get user input values
   * @param {Function} event - Event can be something the browser does, or something a user does
   */
  const onChange = useCallback(
    event => {
      const { name, value } = event.target;
      handleChange(name, value, setErrors, setValues);
    },
    [setValues],
  );

  useEffect(() => {
    /* For detecting auto filled fields on load */
    const inputs = document?.querySelectorAll('input');
    inputs.forEach(input => {
      const { defaultValue } = input;
      if (defaultValue) {
        setDisableCTA(false);
      }
    });
  }, [setDisableCTA]);

  useEffect(() => {
    validateFields(errors, values, setDisableCTA);
  }, [errors, values?.email, values?.password, values, setDisableCTA]);

  useEffect(() => {
    if (enableCaptchaV2?.login) {
      if (captchaV2?.login) {
        setDisableCTA(false);
      } else {
        setDisableCTA(true);
      }
    }
  }, [enableCaptchaV2?.login, captchaV2?.login, setDisableCTA]);
  return (
    <>
      {props?.errorCount === 0 && (
        <>
          <LoginDetails
            ref={fieldsRef}
            errors={{ email: errors.email, password: errors.password }}
            onChange={onChange}
            onBlur={onChange}
            values={{ email: values?.email, password: values?.password }}
          />
          {enableCaptchaV2?.login && !apiError && (
            <Recaptcha
              recaptchaRef={recaptchaRef}
              setCaptchaV2={setCaptchaV2}
              setDisableCTA={setDisableCTA}
            />
          )}
          {apiError?.invalidLogin && (
            <Alert severity='error' sx={style.alert}>
              {apiError?.errorMessage}
            </Alert>
          )}
        </>
      )}
      {errorCount === 1 && (
        <HandleError
          title={TITLE}
          description={DESCRIPTION}
          cta={[{ type: TYPE, name: TRY_AGAIN, action: ERROR_TRY_AGAIN }]}
          handleAction={props.handleAction}
        />
      )}
      {errorCount === maxErrorCount && (
        <HandleError
          title={TITLE}
          description={DESCRIPTION}
          cta={[{ type: TYPE, name: CLOSE, action: ERROR_CLOSE }]}
          handleAction={props.handleAction}
        />
      )}
    </>
  );
};

LoginVerificationContainer.displayName = 'LoginVerificationContainer';
export default memo(LoginVerificationContainer);
